import CloudOffIcon from "@mui/icons-material/CloudOff";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import { useCallback, useState } from "react";

import { PAGE_PADDING } from "@/constants/layout";
import { flushOfflineOrders } from "@/lib/offlineSync";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  setLastSync,
  setOfflineQueue,
  setOfflineSyncing,
} from "@/store/slices/offlineOrdersSlice";
import { localeAmount } from "@/utils/format";

/**
 * "You are offline" / "N orders saved on this device". PRODUCT CHANGE §2.
 *
 * ## Why the wording is what it is
 *
 * The measured failure on the native host was that the browser EVICTED
 * IndexedDB and took a session's queue with it, and the shop read the symptom
 * as "the app doesn't run". So this banner does not say "your order is safe".
 * It says where the order is, what will send it, and — from
 * `requestDurableStorage()`, which reports what the browser actually granted
 * rather than what was asked for — what would still lose it. A customer who is
 * told the truth can act on it (stay on the page, reconnect, don't clear site
 * data); a customer who is told "saved" cannot.
 *
 * It is rendered on the menu and the checkout, the two screens where a basket
 * exists, and it renders NOTHING when the connection is up and the queue is
 * empty — which is the overwhelmingly common case.
 */

const Frame = styled(Box)({
  padding: `0 ${PAGE_PADDING}px`,
  width: "100%",
});

export default function OfflineOrdersBanner() {
  const dispatch = useAppDispatch();
  const { orders, online, syncing, durability, lastError } = useAppSelector(
    (state) => state.offlineOrders,
  );
  const [detailOpen, setDetailOpen] = useState(false);

  const sendNow = useCallback(async () => {
    dispatch(setOfflineSyncing(true));
    try {
      const outcome = await flushOfflineOrders();
      dispatch(setOfflineQueue(outcome.remaining));
      dispatch(setLastSync({ at: Date.now(), error: outcome.error ?? null }));
    } finally {
      dispatch(setOfflineSyncing(false));
    }
  }, [dispatch]);

  const queued = orders.length;
  if (online && queued === 0) return null;

  const total = orders.reduce((sum, row) => sum + row.summary.grand_total, 0);
  const symbol = orders[0]?.summary.currency_symbol ?? "";

  return (
    <Frame>
      <Alert
        severity={online ? "info" : "warning"}
        icon={online ? <CloudQueueIcon /> : <CloudOffIcon />}
        sx={{ alignItems: "flex-start" }}
        action={
          queued > 0 ? (
            <Button
              color="inherit"
              size="small"
              onClick={() => void sendNow()}
              disabled={syncing || !online}
              startIcon={
                syncing ? <CircularProgress size={14} color="inherit" /> : null
              }
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
            >
              {syncing ? "Sending…" : "Send now"}
            </Button>
          ) : undefined
        }
      >
        <AlertTitle sx={{ mb: 0.25 }}>
          {queued > 0
            ? `${queued} order${queued === 1 ? "" : "s"} waiting to be sent`
            : "No connection"}
        </AlertTitle>
        <Typography variant="body2">
          {queued > 0
            ? online
              ? `Saved on this device${
                  total > 0 ? ` (${localeAmount(symbol, total / 100)})` : ""
                }. Sending now — this page can stay open.`
              : `Saved on this device${
                  total > 0 ? ` (${localeAmount(symbol, total / 100)})` : ""
                }. They are sent automatically as soon as the connection is back.`
            : "You can still browse the menu and place an order. It is saved on this device and sent when you are back online."}
        </Typography>

        <Button
          size="small"
          color="inherit"
          onClick={() => setDetailOpen((open) => !open)}
          sx={{ textTransform: "none", px: 0, minWidth: 0, mt: 0.5 }}
          aria-expanded={detailOpen}
        >
          {detailOpen ? "Hide details" : "What does that mean?"}
        </Button>
        <Collapse in={detailOpen} unmountOnExit>
          <Stack spacing={0.75} sx={{ mt: 0.5 }}>
            {/*
              Straight from `requestDurableStorage()`. Three different
              sentences for three genuinely different guarantees — persistent
              storage granted, backup only, or neither — because they are not
              the same promise and must not read as if they were.
            */}
            <Typography variant="caption" component="p">
              {durability?.message ??
                "Orders taken offline are saved on this device and sent when the connection is back."}
            </Typography>
            <Typography variant="caption" component="p">
              An order is only counted once, however many times it is sent: it
              carries an id created on this device, and the restaurant&apos;s
              system updates that same order rather than opening a second one.
            </Typography>
            {durability?.lostPersistence && (
              <Typography variant="caption" component="p" color="error.main">
                This browser previously guaranteed to keep saved orders and no
                longer does. Reconnect as soon as you can.
              </Typography>
            )}
            {lastError && (
              <Typography variant="caption" component="p" color="error.main">
                Last attempt: {lastError}
              </Typography>
            )}
          </Stack>
        </Collapse>
      </Alert>
    </Frame>
  );
}
